// Netherlands used to require a sole proprietorship for individuals.
// This code pattern is reflecting that requirement.
// According to Stripe: "Starting May 14, 2026, connected accounts representing individuals or
// unincorporated entities that don’t have access to a Stripe-hosted Dashboard are no longer
// required to provide a KvK number.
// Connected accounts representing individuals or unincorporated entities that have access to
// the full Stripe Dashboard or the Express Dashboard must still provide a KvK number."
// https://docs.stripe.com/connect/upcoming-requirements-updates?program=eu-2025#netherlands-business-registration-requirements
// Note: this code pattern can still be used by changing the set of countries to `new Set(['NL']);`
// However, since Template works with custom Connect accounts, hiding Stripe dashboard,
// the sole proprietorship requirement is not needed.
const countriesRequiringSoleProprietorshipForIndividuals = new Set([]);

/**
 * Whether Connect onboarding for an individual in this country must use a company account with
 * sole proprietorship structure (Stripe compliance; currently applies to NL).
 *
 * @param {string} country - ISO 3166-1 alpha-2 country code
 * @returns {boolean}
 */
const requiresSoleProprietorshipAccount = country =>
  countriesRequiringSoleProprietorshipForIndividuals.has(country);

/**
 * Builds `business_type` and optional `company.structure` for Stripe account creation or token
 * flows when the seller's country and account type trigger sole-proprietorship rules.
 *
 * @param {Object} params
 * @param {string} params.country - ISO 3166-1 alpha-2 country code
 * @param {string} params.accountType - Stripe business type, e.g. `individual` or `company`
 * @returns {Object} Either `{ business_type: 'company', company: { structure: 'sole_proprietorship' } }`
 *   for individuals in restricted countries, or `{ business_type: accountType }` otherwise
 */
export const getStripeAccountTokenInfo = ({ country, accountType }) => {
  const isIndividualInRestrictedCountry =
    accountType === 'individual' && requiresSoleProprietorshipAccount(country);

  return isIndividualInRestrictedCountry
    ? {
        business_type: 'company',
        company: {
          structure: 'sole_proprietorship',
        },
      }
    : {
        business_type: accountType,
      };
};

/**
 * Normalizes Stripe account data for display: company accounts with sole proprietorship structure
 * are shown as `individual` to match how the seller signed up.
 *
 * @param {Object} [stripeAccountData] - Account object with optional `business_type` and `company.structure`
 * @returns {string|undefined} Display type (`individual` for mapped sole props, otherwise `business_type`, or
 *   `undefined` when missing)
 */
export const getDisplayAccountType = stripeAccountData => {
  const businessType = stripeAccountData?.business_type;
  const companyStructure = stripeAccountData?.company?.structure;

  return businessType === 'company' && companyStructure === 'sole_proprietorship'
    ? 'individual'
    : businessType;
};

export const STRIPE_CONNECT_VERIFICATION_LINK = 'custom_account_verification';
export const STRIPE_CONNECT_UPDATE_LINK = 'custom_account_update';

/**
 * Same-origin Account Link start. iOS Safari blocks async location.assign to
 * connect.stripe.com after the click; a top-level <a href> to this path 302s.
 *
 * @param {Object} params
 * @param {string} params.successURL
 * @param {string} params.failureURL
 * @param {string} [params.type]
 * @param {string} [params.apiBase] marketplace origin, no trailing slash
 * @returns {string}
 */
export const STRIPE_CONNECT_FIELDS_CURRENT = 'currently_due';
export const STRIPE_CONNECT_FIELDS_EVENTUAL = 'eventually_due';

/**
 * Stripe Account Link collection_options.fields.
 *
 * @param {string} [fields]
 * @returns {'currently_due'|'eventually_due'}
 */
export const stripeConnectCollectionFields = fields =>
  fields === STRIPE_CONNECT_FIELDS_EVENTUAL
    ? STRIPE_CONNECT_FIELDS_EVENTUAL
    : STRIPE_CONNECT_FIELDS_CURRENT;

export const buildStripeConnectOnboardingHref = ({
  successURL,
  failureURL,
  type = STRIPE_CONNECT_VERIFICATION_LINK,
  fields,
  apiBase,
} = {}) => {
  const root = String(apiBase || '').replace(/\/$/, '');
  const query = new URLSearchParams({
    successURL: successURL || '',
    failureURL: failureURL || '',
    type,
  });
  if (fields === STRIPE_CONNECT_FIELDS_EVENTUAL) {
    query.set('fields', STRIPE_CONNECT_FIELDS_EVENTUAL);
  }
  return `${root}/api/stripe-connect-onboarding?${query.toString()}`;
};

/**
 * TEST Stripe keys start with pk_test_. Live keys start with pk_live_.
 *
 * @param {string} [key]
 * @returns {boolean}
 */
export const isStripeTestPublishableKey = key =>
  typeof key === 'string' && key.startsWith('pk_test_');

export const getStripeAccountData = stripeAccount =>
  stripeAccount?.attributes?.stripeAccountData || null;

/**
 * True when Stripe still lists fields on this requirement bucket.
 *
 * @param {Object} [stripeAccountData]
 * @param {string} requirementType past_due | currently_due | eventually_due
 * @returns {boolean}
 */
export const hasOpenStripeRequirements = (stripeAccountData, requirementType) =>
  Array.isArray(stripeAccountData?.requirements?.[requirementType]) &&
  stripeAccountData.requirements[requirementType].length > 0;

export const stripeRequirementsBlockPublish = stripeAccountData =>
  hasOpenStripeRequirements(stripeAccountData, 'past_due') ||
  hasOpenStripeRequirements(stripeAccountData, 'currently_due');

/**
 * Stripe Account uses snake_case; some payloads camelCase. Require an explicit true.
 *
 * @param {Object} [stripeAccountData]
 * @param {string} snakeKey
 * @returns {boolean}
 */
export const isStripeCapabilityEnabled = (stripeAccountData, snakeKey) => {
  if (!stripeAccountData || typeof stripeAccountData !== 'object') {
    return false;
  }
  const camelKey = snakeKey.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  return stripeAccountData[snakeKey] === true || stripeAccountData[camelKey] === true;
};

/**
 * Destination charges need transfers (or a legacy stand-in) on the connected account.
 * If Stripe omitted `capabilities`, do not block — charges_enabled + payouts_enabled still apply.
 *
 * @param {Object} [stripeAccountData]
 * @returns {boolean}
 */
export const stripeDestinationTransfersReady = stripeAccountData => {
  if (!stripeAccountData || typeof stripeAccountData !== 'object') {
    return false;
  }
  const caps = stripeAccountData.capabilities;
  if (!caps || typeof caps !== 'object' || Object.keys(caps).length === 0) {
    return true;
  }
  return ['transfers', 'legacy_payments', 'crypto_transfers'].some(key => caps[key] === 'active');
};

/**
 * Sharetribe providers need card charges and payouts (card_payments + transfers).
 *
 * @param {Object} [stripeAccountData]
 * @returns {boolean}
 */
export const stripeConnectCapabilitiesReady = stripeAccountData =>
  isStripeCapabilityEnabled(stripeAccountData, 'charges_enabled') &&
  isStripeCapabilityEnabled(stripeAccountData, 'payouts_enabled') &&
  stripeDestinationTransfersReady(stripeAccountData);

export const getBankAccountLast4Digits = stripeAccountData =>
  stripeAccountData?.external_accounts?.data?.[0]?.last4 || null;

/**
 * Stripe requirement keys that are W-9 / tax ID (SSN or EIN). Never display the values.
 *
 * @param {string} [field]
 * @returns {boolean}
 */
export const isStripeTaxRequirementField = field =>
  typeof field === 'string' && /(ssn|id_number|tax_id|\bein\b)/i.test(field);

const requirementLists = stripeAccountData => {
  const req = stripeAccountData?.requirements || {};
  const future = stripeAccountData?.future_requirements || {};
  return [
    ...(req.currently_due || []),
    ...(req.past_due || []),
    ...(req.eventually_due || []),
    ...(future.currently_due || []),
    ...(future.past_due || []),
    ...(future.eventually_due || []),
  ];
};

/**
 * Tax ID fields Stripe still wants (currently, past, or eventually due).
 *
 * @param {Object} [stripeAccountData]
 * @returns {string[]}
 */
export const stripeTaxRequirementsDue = stripeAccountData =>
  requirementLists(stripeAccountData).filter(isStripeTaxRequirementField);

export const hasOpenStripeTaxRequirements = stripeAccountData =>
  stripeTaxRequirementsDue(stripeAccountData).length > 0;

/**
 * ISO country on the Connect account, or the saved country from the first step.
 *
 * @param {Object} [stripeAccountData]
 * @param {string} [savedCountry]
 * @returns {string|null}
 */
export const stripeAccountCountry = (stripeAccountData, savedCountry) => {
  const country = stripeAccountData?.country || savedCountry;
  return typeof country === 'string' && country.trim()
    ? country.trim().toUpperCase()
    : null;
};

/**
 * Tax info (1099) is US reporting. Show for Listen / Both when country is US or not chosen yet.
 *
 * @param {Object} [params]
 * @param {boolean} [params.showPayoutDetails]
 * @param {Object} [params.stripeAccountData]
 * @param {string} [params.savedCountry]
 * @returns {boolean}
 */
export const showStripeTax1099Section = ({
  showPayoutDetails = false,
  stripeAccountData,
  savedCountry,
} = {}) => {
  if (!showPayoutDetails) {
    return false;
  }
  const country = stripeAccountCountry(stripeAccountData, savedCountry);
  return !country || country === 'US';
};

export const isStripeAccountConnected = ({ currentUser, stripeAccount } = {}) =>
  !!(currentUser?.stripeAccount?.id || stripeAccount?.id);

export const STRIPE_REQUIRED_CODE = 'stripe_required';

/**
 * @param {string} [message]
 * @returns {Error}
 */
export const stripeRequiredError = (
  message = 'Finish Stripe Get verified before publishing a Listening profile.'
) => {
  const error = new Error(message);
  error.status = 403;
  error.code = STRIPE_REQUIRED_CODE;
  error.statusText = STRIPE_REQUIRED_CODE;
  return error;
};

/**
 * Publish a paid Listening profile only when Stripe Connect is fully ready.
 * Same on TEST and Live — a Connect account existing is not enough.
 *
 * Ready means:
 * - payout details are required for this listing type (hosted payoutDetails: false skips)
 * - a Connect account is linked
 * - charges_enabled and payouts_enabled are true
 * - requirements.currently_due and requirements.past_due are empty
 *
 * Draft / edit stays allowed. eventually_due does not block.
 *
 * @param {Object} [params]
 * @returns {boolean}
 */
export const stripeAccountReadyToPublish = ({
  currentUser,
  stripeAccount,
  listingTypeConfig,
  payoutDetailsRequired,
} = {}) => {
  const required =
    payoutDetailsRequired != null
      ? payoutDetailsRequired
      : listingTypeConfig?.defaultListingFields?.payoutDetails !== false;
  if (!required) {
    return true;
  }
  if (!isStripeAccountConnected({ currentUser, stripeAccount })) {
    return false;
  }
  const stripeAccountData = getStripeAccountData(stripeAccount);
  if (!stripeAccountData) {
    return false;
  }
  return (
    stripeConnectCapabilitiesReady(stripeAccountData) &&
    !stripeRequirementsBlockPublish(stripeAccountData)
  );
};

/**
 * Persist whether this Listening profile can accept Book / Start now.
 * Never write false unless the caller confirmed a successful Stripe
 * account fetch (`persistFalse: true`). A 429 or empty payload must
 * not mark a published listing unbookable.
 *
 * @param {Object} sdk
 * @param {Object} listing
 * @param {boolean} payoutsReady
 * @param {Object} [options]
 * @param {boolean} [options.persistFalse] allow writing payoutsReady: false
 * @returns {Promise}
 */
export const syncOwnListingPayoutsReady = (sdk, listing, payoutsReady, options = {}) => {
  const raw = listing?.id;
  const id = raw?.uuid ? raw : raw ? { uuid: String(raw) } : null;
  if (!id || !sdk?.ownListings?.update) {
    return Promise.resolve(null);
  }
  const ready = !!payoutsReady;
  if (!ready && options.persistFalse !== true) {
    return Promise.resolve(null);
  }
  const current = listing?.attributes?.publicData?.payoutsReady;
  if (current === ready) {
    return Promise.resolve(null);
  }
  return sdk.ownListings
    .update({ id, publicData: { payoutsReady: ready } })
    .catch(() => null);
};

export const isStripeHostedOrigin = origin => {
  try {
    const host = new URL(origin).hostname;
    return /(^|\.)stripe\.com$/i.test(host);
  } catch (e) {
    return /stripe\.com/i.test(String(origin || ''));
  }
};

/**
 * Return URLs for Account Links must be this marketplace — never connect.stripe.com.
 * If the UI is framed on Stripe, window.location.origin is the wrong root.
 *
 * @param {string} [configuredRoot]
 * @param {Location} [location]
 * @returns {string}
 */
export const stripeConnectReturnRootURL = (
  configuredRoot,
  location = typeof window !== 'undefined' ? window.location : undefined
) => {
  const origin = location?.origin;
  if (origin && !isStripeHostedOrigin(origin)) {
    return origin.replace(/\/$/, '');
  }
  return String(configuredRoot || '').replace(/\/$/, '');
};

/**
 * Leave iframes / Stripe-hosted shells. Same-window assign, then _top.
 *
 * @param {string} url
 * @param {Window} [win]
 * @returns {boolean}
 */
export const assignTopLevelLocation = (
  url,
  win = typeof window !== 'undefined' ? window : undefined
) => {
  if (!url || typeof url !== 'string' || !win) {
    return false;
  }
  const target = win.top && !win.top.closed ? win.top : win;
  try {
    if (typeof target.location.assign === 'function') {
      target.location.assign(url);
    } else {
      target.location.href = url;
    }
    return true;
  } catch (e) {
    try {
      win.open(url, '_top');
      return true;
    } catch (openError) {
      return false;
    }
  }
};

/**
 * Create a Stripe Account Link and navigate to it (top window).
 *
 * @param {Object} params
 * @returns {Promise<string>}
 */
export const startStripeConnectOnboarding = ({
  getLinkFn,
  type,
  successURL,
  failureURL,
  accountId,
  fields,
  assignLocation = assignTopLevelLocation,
}) => {
  if (typeof getLinkFn !== 'function') {
    return Promise.reject(new Error('Missing Stripe account-link handler'));
  }
  return Promise.resolve(
    getLinkFn({
      type,
      successURL,
      failureURL,
      accountId,
      fields: stripeConnectCollectionFields(fields),
    })
  ).then(url => {
    if (!url || typeof url !== 'string') {
      throw new Error('Stripe did not return an onboarding URL');
    }
    const ok = assignLocation(url);
    if (!ok) {
      throw new Error('Could not open Stripe onboarding');
    }
    return url;
  });
};

/**
 * Click handler factory used by Get verified / Edit account.
 *
 * @param {Function} getLinkFn
 * @param {Object} commonParams
 * @returns {function(string): function(Event=): Promise}
 */
export const handleGetStripeConnectAccountLinkFn = (getLinkFn, commonParams) => type => event => {
  if (event?.preventDefault) {
    event.preventDefault();
  }
  if (event?.stopPropagation) {
    event.stopPropagation();
  }
  return startStripeConnectOnboarding({
    getLinkFn,
    type,
    ...commonParams,
  }).catch(err => {
    if (typeof console !== 'undefined' && console.error) {
      console.error(err);
    }
    throw err;
  });
};
